import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  CreateAxiosDefaults,
  InternalAxiosRequestConfig,
} from 'axios';

import {
  coalesceTokenRenewal,
  handleTokenFromConfig,
  matchesOriginWithCredentials,
} from './custom-axios.helper';

declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    /** Set once a request has been retried after a token renewal, to prevent retry loops. */
    _retry?: boolean;
  }
}

/** Silent-renew callback invoked on a 401; resolves to a fresh access token (nullish on failure). */
export type RenewTokenFn = () => Promise<string | null | undefined>;

export type CustomAxiosOptions = CreateAxiosDefaults & {
  authToken?: string;
  tokenFromStorage?: 'local' | 'session';
  /**
   * Origins whose requests are sent with cookies (withCredentials), e.g. the HR
   * instance, which resolves the current persona from the xemploSessionId
   * session cookie. Other origins keep the axios default.
   */
  originsWithCredentials?: string[];
  /** Called on a 401 to silently renew the token before a single retry. */
  onUnauthorized?: RenewTokenFn;
  /** Called when the renewal fails; expected to clear stale auth (the app's auth guard then routes to login). */
  onAuthFailure?: () => void | Promise<void>;
};

export class CustomAxios {
  private _client: AxiosInstance;
  private _config?: CustomAxiosOptions;

  constructor(config?: CustomAxiosOptions) {
    this._client = axios.create(config);
    this._config = config;
    this.addRequestInterceptor();
    this.addResponseInterceptor();
  }

  client() {
    return this._client;
  }

  private addRequestInterceptor() {
    this._client.interceptors.request.use(async (request) => {
      const headers = new AxiosHeaders();

      // The HR instance resolves the current persona from the session cookie, so
      // requests to such an origin must send it. Only ever opts in, so an
      // explicit caller-set withCredentials is preserved.
      if (
        matchesOriginWithCredentials(request.url, this._config?.originsWithCredentials)
      ) {
        request.withCredentials = true;
      }

      if (request.url?.includes('profile-photo')) {
        headers.set('Accept', 'image/*');
        headers.set('Content-Type', 'image/*');
        headers.set('Authorization', 'Sass-Key abcd');
        request.headers = request.headers
          ? Object.assign(request.headers, headers)
          : headers;

        return request;
      }

      // On a retry the renewed token is already set on the request (retryWithToken);
      // re-applying the config token here would leave the renewed one to win only by
      // header-merge order, so skip it and let the request's own token stand.
      if (!request._retry) {
        headers.set('Authorization', `Bearer ${handleTokenFromConfig(this._config)}`);
      }
      headers.set('Accept', 'application/json');
      headers.set('Content-Type', 'application/json');

      if (request.data instanceof FormData) {
        headers.set('Content-Type', 'multipart/form-data');
      }

      request.headers = Object.assign(headers, request.headers);

      return request;
    });
  }

  private addResponseInterceptor() {
    this._client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => this.handleResponseError(error),
    );
  }

  private async handleResponseError(error: AxiosError) {
    const request = error.config;
    const { onUnauthorized, onAuthFailure } = this._config ?? {};

    // Only handle 401s with a configured recovery; everything else bubbles up.
    if (error.response?.status !== 401 || !request || !onUnauthorized) {
      return Promise.reject(error);
    }

    // Already retried and still 401: a freshly renewed token is also rejected
    // (endpoint-level, not a dead session) — surface it rather than logging out.
    if (request._retry) {
      return Promise.reject(error);
    }

    request._retry = true;

    // Only a failed renewal (no token) means the session is genuinely gone → re-auth.
    const renewedToken = await coalesceTokenRenewal(onUnauthorized);
    if (!renewedToken) {
      await onAuthFailure?.();
      return Promise.reject(error);
    }

    return this.retryWithToken(request, renewedToken);
  }

  private retryWithToken(request: InternalAxiosRequestConfig, token: string) {
    const headers = AxiosHeaders.from(request.headers);
    headers.set('Authorization', `Bearer ${token}`);
    request.headers = headers;
    return this._client(request);
  }
}
